import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";

const LINKS = [
  { href: "#home", label: "Index" },
  { href: "#portfolio", label: "Work" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#graphic-design", label: "Graphics" },
  { href: "#blog", label: "Journal" },
  { href: "#contact", label: "Contact" },
];

export default function Nav({ theme, setTheme }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#0B0E14]/70 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 md:px-10 py-5">
          <a href="#home" data-testid="nav-logo" className="font-display text-xl md:text-2xl tracking-tight text-[#F2EBE5]">
            Saanvi<span className="text-[#D18A58]">.</span>
          </a>
          <ul className="hidden md:flex items-center gap-9 text-[11px] uppercase tracking-overline text-[#F2EBE5]/80">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="relative group hover:text-[#F2EBE5]"
                >
                  {l.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D18A58] transition-all group-hover:w-full" />
                </a>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <button
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#F2EBE5]/15 text-[#F2EBE5] hover:bg-[#F2EBE5]/10"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <a
              href="#contact"
              data-testid="nav-cta-btn"
              className="hidden md:inline-flex rounded-full bg-[#D18A58] px-5 py-2 text-[11px] uppercase tracking-overline text-[#0B0E14] font-medium hover:bg-[#F2EBE5]"
            >
              Let's Talk
            </a>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#F2EBE5]/15 text-[#F2EBE5]"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.55, ease: [0.7, 0, 0.2, 1] }}
            className="fixed inset-0 z-[60] bg-[#0B0E14] text-[#F2EBE5]"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <span className="font-display text-xl">Saanvi<span className="text-[#D18A58]">.</span></span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#F2EBE5]/15"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="px-6 py-8 space-y-2">
              {LINKS.map((l, i) => (
                <motion.li
                  key={l.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1 }}
                >
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-link-${l.label.toLowerCase()}`}
                    className="block font-display text-5xl py-3 hover:text-[#D18A58]"
                  >
                    {l.label}
                  </a>
                </motion.li>
              ))}
            </ul>
            <div className="px-6 mt-6 flex items-center gap-3">
              <button
                data-testid="mobile-theme-toggle"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="inline-flex h-11 items-center gap-2 rounded-full border border-[#F2EBE5]/15 px-4 text-xs uppercase tracking-overline"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === "dark" ? "Light" : "Dark"} Mode
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
